import type { ReactNode } from 'react';
import React, { createContext, useContext, useState, useEffect } from 'react';
import type { Theme } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';

interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
  theme: Theme;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Default to dark for new visitors; respect any saved preference.
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : true;
  });

  // Save theme preference to localStorage
  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const theme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: '#1976d2',
        light: '#42a5f5',
        dark: '#1565c0',
      },
      secondary: {
        main: '#dc004e',
        light: '#ff5983',
        dark: '#9a0036',
      },
      background: {
        default: isDarkMode ? '#121212' : '#fafafa',
        paper: isDarkMode ? '#1e1e1e' : '#ffffff',
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontWeight: 700, fontSize: 'clamp(2rem, 5vw, 3rem)' },
      h2: { fontWeight: 600, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)' },
      h3: { fontWeight: 600, fontSize: 'clamp(1.5rem, 3.5vw, 2.125rem)' },
      h4: { fontWeight: 600, fontSize: 'clamp(1.35rem, 3vw, 1.75rem)' },
      h5: { fontWeight: 500, fontSize: 'clamp(1.15rem, 2.5vw, 1.4rem)' },
      h6: { fontWeight: 500, fontSize: 'clamp(1rem, 2vw, 1.15rem)' },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: isDarkMode
              ? '0 4px 20px rgba(0,0,0,0.3)'
              : '0 4px 20px rgba(0,0,0,0.1)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 500,
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          },
        },
      },
      // 44px is the WCAG-recommended touch target. Cascades to the sidebar nav
      // and any list-driven UI without per-button overrides.
      MuiListItemButton: {
        styleOverrides: {
          root: {
            minHeight: 44,
          },
        },
      },
      // Make Dialogs full-screen on phones without each call-site needing
      // useMediaQuery + the fullScreen prop. Larger viewports keep MUI defaults.
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme: t }) => ({
            [t.breakpoints.down('sm')]: {
              margin: 0,
              maxWidth: '100%',
              width: '100%',
              // Use dynamic viewport height so iOS Safari's URL bar can't push
              // DialogActions / scroll bottom under the chrome.
              maxHeight: '100dvh',
              height: '100dvh',
              borderRadius: 0,
              paddingBottom: 'env(safe-area-inset-bottom)',
            },
          }),
        },
      },
      // Default IconButton size 40x40 is below tap-target. In-table compact
      // icons opt out via size="small" (MUI's small variant is 34px which we
      // accept inside dense rows; primary chrome uses default).
      MuiIconButton: {
        variants: [
          {
            props: { size: 'medium' },
            style: { minWidth: 44, minHeight: 44 },
          },
        ],
      },
    },
  });

  const value = {
    isDarkMode,
    toggleTheme,
    theme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};








